import {
  retrieveSeriesMetadata,
  retrieveStudyMetadata,
  type Instance,
} from '@/src/core/dicom-web-api';

export interface OriginalPanoramaImage {
  studyUID: string;
  seriesUID: string;
  instanceUID: string;
  label: string;
}

export function isPanoramicRadiograph(info: Partial<Instance>): boolean {
  const modality = (info.Modality ?? '').trim().toUpperCase();
  // CT, localizers and reconstructed CPR are not the original radiograph.
  if (!['PX', 'DX', 'CR', 'RG', 'OT', 'SC', ''].includes(modality))
    return false;
  const description = (info.SeriesDescription ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (
    /\b(cpr|mpr|cbct|scout|localizer|topogram|ceph\w*|cefal\w*|telerad\w*)\b/.test(
      description
    )
  )
    return false;
  if (modality === 'PX') return true;
  return /\b(panoram\w*|orthopantom\w*|ortopantom\w*|opg|pano|pan)\b/.test(
    description
  );
}

export async function findOriginalPanoramas(
  root: string,
  studyUID: string
): Promise<OriginalPanoramaImage[]> {
  const series = await retrieveStudyMetadata(root, {
    studyInstanceUID: studyUID,
  });
  const candidates = series.filter(
    (info) =>
      info.SeriesInstanceUID &&
      (!info.StudyInstanceUID || info.StudyInstanceUID === studyUID) &&
      isPanoramicRadiograph(info)
  );
  const seen = new Set<string>();
  const images: OriginalPanoramaImage[] = [];
  for (const candidate of candidates) {
    if (seen.has(candidate.SeriesInstanceUID)) continue;
    seen.add(candidate.SeriesInstanceUID);
    const instances = await retrieveSeriesMetadata(root, {
      studyInstanceUID: studyUID,
      seriesInstanceUID: candidate.SeriesInstanceUID,
    });
    instances
      .filter((info) => {
        if (info.StudyInstanceUID && info.StudyInstanceUID !== studyUID)
          return false;
        if (
          info.SeriesInstanceUID &&
          info.SeriesInstanceUID !== candidate.SeriesInstanceUID
        )
          return false;
        return (
          info.SopInstanceUID &&
          Number(info.Rows) > 0 &&
          Number(info.Columns) > 0 &&
          Number(info.NumberOfFrames ?? 1) === 1 &&
          isPanoramicRadiograph({ ...candidate, ...info })
        );
      })
      .sort(
        (a, b) => Number(a.InstanceNumber ?? 0) - Number(b.InstanceNumber ?? 0)
      )
      .forEach((info, index) => {
        images.push({
          studyUID,
          seriesUID: candidate.SeriesInstanceUID,
          instanceUID: info.SopInstanceUID,
          label: `${info.SeriesDescription || candidate.SeriesDescription || 'Panorámica'}${instances.length > 1 ? ` · Imagen ${index + 1}` : ''}`,
        });
      });
  }
  return images;
}
